import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { toast } from 'react-toastify';
import { LectureWithSessions } from '../../../../../api/types/programs/course/lectures/lecture-with-schedule-metadata';
import { updateLectureBreakAndAttendanceOperation } from '../operations/update-lecture-break-and-attendance-operation';

interface EditLectureBreakAttendanceDialogProps {
  visible: boolean;
  lecture: LectureWithSessions | null;
  onHide: () => void;
}

const attendanceSessionOptions = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
];

export const EditLectureBreakAttendanceDialog: React.FC<
  EditLectureBreakAttendanceDialogProps
> = ({ visible, lecture, onHide }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [attendanceSessionNumber, setAttendanceSessionNumber] = useState<
    number | null
  >(1);
  const [addBreak, setAddBreak] = useState(false);
  const [breakMinutes, setBreakMinutes] = useState<number | null>(null);

  useEffect(() => {
    if (!visible || !lecture) {
      return;
    }

    const currentBreakMinutes = lecture.item.lectureBreakMinutes;
    setAttendanceSessionNumber(lecture.item.attendanceSessionNumber ?? 1);
    setAddBreak(
      currentBreakMinutes !== null && currentBreakMinutes !== undefined,
    );
    setBreakMinutes(
      currentBreakMinutes !== null && currentBreakMinutes !== undefined
        ? currentBreakMinutes
        : null,
    );
  }, [visible, lecture]);

  const handleApprove = async () => {
    if (!lecture) {
      return;
    }

    if (lecture.item.attendanceRequired) {
      if (
        attendanceSessionNumber === null ||
        !Number.isInteger(attendanceSessionNumber) ||
        attendanceSessionNumber < 1 ||
        attendanceSessionNumber > 3
      ) {
        toast.warn('יש לבחור מספר מפגש נוכחות בין 1 ל-3');
        return;
      }
    }

    if (addBreak) {
      if (
        breakMinutes === null ||
        breakMinutes === undefined ||
        !Number.isInteger(breakMinutes) ||
        breakMinutes < 0
      ) {
        toast.warn('יש להזין מספר דקות הפסקה תקין (מספר שלם 0 או יותר)');
        return;
      }
    }

    await updateLectureBreakAndAttendanceOperation(
      lecture.item.id,
      lecture.item.attendanceRequired ? attendanceSessionNumber : null,
      addBreak ? breakMinutes : null,
      setIsLoading,
      onHide,
    );
  };

  const dialogFooter = (
    <div>
      <Button
        label="ביטול"
        icon="pi pi-times"
        onClick={onHide}
        className="p-button-text"
        disabled={isLoading}
      />
      <Button
        label="אישור"
        icon="pi pi-check"
        onClick={handleApprove}
        loading={isLoading}
        disabled={isLoading}
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      header="עריכת פרטי הרצאה"
      visible={visible}
      style={{ width: '480px' }}
      footer={dialogFooter}
      onHide={() => {
        if (!isLoading) {
          onHide();
        }
      }}
      closable={!isLoading}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '16px',
        }}
      >
        {lecture?.item.attendanceRequired && (
          <div
            style={{
              height: '70px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <label
              htmlFor="edit-lecture-attendance-session"
              style={{
                fontSize: '14px',
                color: '#4b5563',
                whiteSpace: 'nowrap',
                height: '22px',
              }}
            >
              מספר מפגש נוכחות
            </label>
            <Dropdown
              inputId="edit-lecture-attendance-session"
              value={attendanceSessionNumber}
              onChange={(e) => setAttendanceSessionNumber(e.value)}
              options={attendanceSessionOptions}
              optionLabel="label"
              optionValue="value"
              disabled={isLoading}
              style={{
                height: '36px',
                width: '120px',
                alignItems: 'center',
              }}
            />
          </div>
        )}

        <div
          style={{
            display: 'flex',
            gap: '12px',
            flexDirection: 'column',
            alignItems: 'center',
            flexWrap: 'nowrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '8px',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <Checkbox
              inputId="edit-lecture-add-break-checkbox"
              checked={addBreak}
              disabled={isLoading}
              onChange={(e) => {
                const next = Boolean(e.checked);
                setAddBreak(next);
                if (!next) {
                  setBreakMinutes(null);
                } else if (
                  breakMinutes === null ||
                  breakMinutes === undefined
                ) {
                  setBreakMinutes(0);
                }
              }}
            />
            <label
              htmlFor="edit-lecture-add-break-checkbox"
              style={{
                fontSize: '14px',
                color: '#4b5563',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }}
            >
              דקות הפסקה
            </label>
          </div>

          <InputNumber
            value={breakMinutes}
            onChange={(e) =>
              setBreakMinutes(e.value === undefined ? null : e.value)
            }
            min={0}
            showButtons
            useGrouping={false}
            disabled={!addBreak || isLoading}
            inputStyle={{ textAlign: 'right', width: '60px' }}
            style={{
              height: '36px',
              width: '120px',
              direction: 'ltr',
              flexShrink: 0,
            }}
          />
        </div>
      </div>
    </Dialog>
  );
};
